import React, {useContext, useEffect, useRef, useState} from 'react';
import {BrowserRouter, Navigate, Route, Routes} from "react-router-dom";
import Navigation from "./components/Navigation";
import CreateIntern from "./pages/CreateIntern";
import NotFound from "./pages/NotFound";
import SpaceDetails from "./pages/SpaceDetails";
import {useIsAuthenticated, useMsal, useMsalAuthentication} from "@azure/msal-react";
import {InteractionStatus, InteractionType} from "@azure/msal-browser";
import {AppContext} from "./context/AppContext";
import {AnimatePresence} from "framer-motion";
import {toast, Toaster} from "react-hot-toast";
import useApi from "./hooks/useApi";
import {GroupsContext} from "./context/GroupsContext";
import Loading from "./components/Loading";
import SpaceAcceptance from "./pages/SpaceAcceptance";
import CreateCustomers from "./pages/CreateCustomers";
import Admin from "./pages/Admin";
import ProtectedRoute from "./pages/ProtectedRoute";
import Flows from "./pages/admin/Flows";
import FlowPolicies from "./pages/admin/FlowPolicies";
import {AdminProvider} from "./context/AdminContext";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";
import SpaceEdit from "./pages/SpaceEdit";
import Units from "./pages/admin/Units";
import Logs from "./pages/admin/Logs";
import Settings from "./pages/admin/Settings";
import Actions from "./pages/admin/Actions";
import axios from "axios";
import OwnerChange from "./pages/OwnerChange";
import HomePage from "./pages/HomePage";
import { useSessionStorage } from "react-use";
import useAuthSocket from "./hooks/useAuthSocket";
import {IGroup} from "./types/ApiTypes";

type IdTokenClaims = {
  roles: string[];
  exp: number;
}

function App() {
  const mounted = useRef(false);
  const [error, setError] = useState('');
  const msalAuth = useMsalAuthentication(InteractionType.Redirect, {
    redirectUri: "/create/intern",
    scopes: ["api://5d1f1f34-2363-4dcc-8e38-501ea17ccd10/Test.ReadWrite"]
  });
  const [tokenTimer, setTokenTimer] = useState<NodeJS.Timeout>();
  const [tokenExp, setTokenExp] = useState<number>();
  const [tokenAcquired, setTokenAcquired] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const [token, setToken] = useSessionStorage<string>('token','',true);
  const appCtx = useContext(AppContext);
  const groupCtx = useContext(GroupsContext);
  const msal = useMsal();
  const {socket} = useAuthSocket();
  const {fetchUserGroups, fetchCompanyEmployees, fetchCompanies, fetchProjects, fetchCompanyUnits, fetchAppSettings} = useApi();

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    console.log('Expires in',tokenExp)
    if(!tokenExp) return;
    const timeout = setTimeout(() => {
      msalAuth.acquireToken(InteractionType.Silent).then(payload => {
        if (mounted.current) {
          setToken(payload?.accessToken || '');
          if (payload?.expiresOn) setTokenExp(payload.expiresOn.getTime() - new Date().getTime() - 30000);
          if (payload?.idTokenClaims) appCtx.dispatch({
            type: 'SET_ROLES',
            payload: (payload.idTokenClaims as IdTokenClaims).roles
          })
          setTokenAcquired(true)
          console.log(payload?.idTokenClaims);
        }
      })
    },tokenExp);
    return () => {
      clearTimeout(timeout);
    };
  },[tokenExp])
  
  useEffect(() => {
    if(msal.inProgress === InteractionStatus.None) {
      if (isAuthenticated) {
        msal.instance.setActiveAccount(msal.accounts[0]);
        msalAuth.acquireToken().then(payload => {
          console.log(payload);
          if (mounted.current) {
            if (payload?.accessToken) {
              console.log('Access token acquired', payload)
              setToken(payload.accessToken);
              setTokenAcquired(true);
              if (payload.expiresOn) setTokenExp(payload.expiresOn.getTime() - new Date().getTime() - 30000);
            }
            if (payload?.idTokenClaims) appCtx.dispatch({
              type: 'SET_ROLES',
              payload: (payload.idTokenClaims as IdTokenClaims).roles
            })
            console.log(payload?.idTokenClaims);
          }
        }).catch((e) => {
          console.error(e);
        })

      } else {
        msalAuth.login();
      }
    }
    return () => {
      if (tokenTimer) {
        clearTimeout(tokenTimer);
        setTokenTimer(undefined);
      }
    }
  }, [isAuthenticated, msal.instance, msal.accounts, msal.inProgress])

  useEffect(() => {
    if (mounted.current && isAuthenticated && token && tokenAcquired) {
      Promise.all([
        fetchUserGroups(),
        fetchCompanyEmployees(),
        fetchCompanies(),
        fetchCompanyUnits(),
        fetchAppSettings(),
        fetchProjects()
      ]).then(res => {
        if (res.length === 6 && mounted.current) {
          groupCtx.dispatch({type: 'SET_GROUPS', payload: res[0] ? res[0] : []})
          appCtx.dispatch({type: 'SET_USER', payload: res[1] ? res[1] : []})
          if (res[2]) groupCtx.dispatch({type: 'SET_CUSTOMERS', payload: res[2]})
          appCtx.dispatch({
            type: 'SET_UNITS', payload: {
              main: res[3].main,
              secondary: res[3].secondary
            }
          });
          if (res[4]) appCtx.dispatch({type: 'SET_SETTINGS', payload: res[4]});
          if (res[5]) appCtx.dispatch({type: 'SET_PROJECTS', payload: res[5]});
          setError('');
        }
      }).catch(e => {
        // setTimeout(() => {
        //   window.location.reload();
        // }, 3000);
        setError(e.message);
      });
    }
  }, [fetchUserGroups, fetchCompanyEmployees, fetchCompanies, fetchCompanyUnits, fetchAppSettings, appCtx.dispatch, groupCtx.dispatch, isAuthenticated, token, tokenAcquired])

  useEffect(() => {
    axios.interceptors.response.use((res) => res, err => {
      toast.error(err.toString())
    })
  }, [])

  // Update spaces on change
  useEffect(() => {
    socket.off('SPACE_STATUS_CHANGED');
    socket.on('SPACE_STATUS_CHANGED', (space: IGroup) => {
      console.log(space);
      const userSpace = groupCtx.state.groups.find(x => x.requestId === space.requestId);
      if (!userSpace) return;
      groupCtx.dispatch({type: "REPLACE_GROUP", payload: space})
    })
    return () => {
      socket.off('SPACE_STATUS_CHANGED')
    }
  }, [socket, groupCtx.state.groups])

  return (
    <BrowserRouter>
      <DndProvider backend={HTML5Backend}>
        <Toaster position='top-right'/>
        { tokenAcquired ? (
          <div className="wrapper">
            <Navigation/>
            <div className="right">
              <AnimatePresence>
                <Routes>
                  <Route path='/' element={<HomePage/>}/>
                  <Route path='/admin' element={
                    <ProtectedRoute requiredRole='ROLE_ADMIN' roles={appCtx.state.roles}>
                      <AdminProvider>
                        <Admin/>
                      </AdminProvider>
                    </ProtectedRoute>
                  }>
                    <Route path='flows' element={<Flows/>}/>
                    <Route path='flow_policies' element={<FlowPolicies/>}/>
                    <Route path='logs' element={<Logs/>}/>
                    <Route path='units' element={<Units/>}/>
                    <Route path='actions' element={<Actions/>}/>
                    <Route path='settings' element={<Settings/>}/>
                    <Route path='' element={<Navigate replace to={'/admin/flows'}/>}/>
                  </Route>
                  <Route path='/create/intern' element={<CreateIntern/>}/>
                  <Route path='/create/customers' element={<CreateCustomers/>}/>
                  <Route path='/space/acceptance/:id' element={<SpaceAcceptance/>}/>
                  <Route path='/space/:id/owner-change/' element={<OwnerChange/>}/>
                  <Route path='/space/edit/:id' element={<SpaceEdit/>}/>
                  <Route path='/space/:id' element={<SpaceDetails/>}/>
                  <Route path='*' element={<NotFound/>}/>
                </Routes>
              </AnimatePresence>
            </div>
          </div>
        ) : (
          <Loading error={error}/>
        )}
      </DndProvider>
    </BrowserRouter>
  );
}

export default App;
